﻿import React, { useRef, useState } from "react";
import { Box, Card, Container } from "@mui/material";
import "./GlobalSettings.css";
import EditQualifiersReadOptions from "./EditQualifiersReadOptions";
import EditQualifiersItems from "./EditQualifiersItems";
import EditQualifierISAGS from "./EditQualifiersISAGS";
import CounterSetup from "./CounterSetup";
import { useDispatch, useSelector } from "react-redux";
import {
  appTypesEnum,
  selectSelectedConnection,
  setTransactionSettingsDataChangeDetected,
} from "@/features/connections/integrationSlice";
import { FooterButton } from "@/components/Integrations/ManageConnection/ManageConnectionPopupContext";
import { createAlert } from "@/features/homepage/alertSliceList";
import axios from "@/features/axiosHelper";

const GlobalSettings = () => {
  const selectedConnection = useSelector(selectSelectedConnection);
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);

  //Subcomponent refs for validation and data retrieval
  const isagsRef = useRef();
  const readOptionsRef = useRef();
  const itemsRef = useRef();
  const counterRef = useRef();

  const CheckIfConnectionIsFTP = () => {
    if (
      selectedConnection.EndpointType === appTypesEnum.RetailChannel ||
      selectedConnection.EndpointType === appTypesEnum.WMS
    ) {
      return true;
    } else {
      return false;
    }
  };

  const handleSaveAll = async () => {
    if (saving) return;

    // Client-side validation
    const validationErrors = [];

    const isagsError = isagsRef.current?.validate();
    if (isagsError) validationErrors.push(isagsError);

    const itemsError = itemsRef.current?.validate();
    if (itemsError) validationErrors.push(itemsError);

    if (CheckIfConnectionIsFTP() && counterRef.current) {
      const counterError = counterRef.current.validate();
      if (counterError) validationErrors.push(counterError);
    }

    if (validationErrors.length > 0) {
      dispatch(
        createAlert({
          status: "Error",
          alertType: "result",
          message: validationErrors.join("\n"),
          buttonLabel: "Close",
        })
      );
      return;
    }

    // Collect data from all components
    const connectionSettings = isagsRef.current?.getFormData();
    const readOptionsData = readOptionsRef.current?.getFormData();
    const itemQualifiersData = itemsRef.current?.getFormData();

    let referenceCounterData = null;
    if (CheckIfConnectionIsFTP() && counterRef.current) {
      referenceCounterData = counterRef.current.getFormData();
    }

    const payload = {
      ConnectionId: selectedConnection.ConnectionId.toString(),
      ArtifactType: -1,
      EndpointSchemaId: 0,
      IsTransactionSettings: false,
      ConnectionSettings: connectionSettings,
      ReadOptionsString: JSON.stringify(readOptionsData),
      ItemQualifiersString: JSON.stringify(itemQualifiersData),
      ReferenceCounter: referenceCounterData,
    };

    setSaving(true);
    try {
      const response = await axios.post(
        "/connectionartifactschema/save-all-global-settings",
        payload
      );

      if (response.data?.Success) {
        if (isagsRef.current?.onSaveSuccess) {
          isagsRef.current.onSaveSuccess();
        }
        if (counterRef.current?.onSaveSuccess) {
          counterRef.current.onSaveSuccess();
        }

        dispatch(
          setTransactionSettingsDataChangeDetected({
            transactionSettingsDataChangeDetected: false,
          })
        );

        window.heap.track("Global Settings - Save", {
          "Settings Subgroup": "All",
        });

        if (response.data?.CounterWarning) {
          dispatch(
            createAlert({
              status: "Warning",
              alertType: "result",
              message: response.data.CounterWarning,
              buttonLabel: "Close",
            })
          );
        } else {
          dispatch(
            createAlert({
              status: "Success",
              alertType: "result",
              message: "All settings were saved successfully.",
              buttonLabel: "Close",
            })
          );
        }
      } else {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "result",
            message: "There was an error while saving settings.",
            buttonLabel: "Close",
          })
        );
      }
    } catch (error) {
      let errData = error.response?.data;
      let errMsg;
      if (typeof errData === "string") {
        errMsg = errData;
      } else if (errData?.errors) {
        errMsg = Object.entries(errData.errors)
          .map(
            ([field, msgs]) =>
              `${field}: ${Array.isArray(msgs) ? msgs.join(", ") : msgs}`
          )
          .join("\n");
      } else if (errData?.title) {
        errMsg = errData.title;
      } else {
        errMsg = "An unexpected error occurred while saving settings.";
      }
      dispatch(
        createAlert({
          status: "Error",
          alertType: "result",
          message: errMsg,
          buttonLabel: "Close",
        })
      );
      console.error("Error during save:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <Box>
        <FooterButton variant="primary" onClick={handleSaveAll} disabled={saving}>
          Save
        </FooterButton>
        <EditQualifierISAGS ref={isagsRef} isTransactionSettings={false} />
        <Card className="global-settings-card">
          <EditQualifiersReadOptions
            ref={readOptionsRef}
            isTransactionSettings={false}
          />
        </Card>
        <Card className="global-settings-card">
          <EditQualifiersItems ref={itemsRef} isTransactionSettings={false} />
        </Card>
        {CheckIfConnectionIsFTP() ? (
          <>
            <Card className="global-settings-card">
              <CounterSetup ref={counterRef} />
            </Card>
          </>
        ) : (
          ""
        )}
      </Box>
    </Container>
  );
};
export default GlobalSettings;
